import { ReactElement } from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { CareerPath, Tier } from '@backrs_ui/common-ui/src/models/careerPath';
import {
  ASK_LUBAV_BUTTON_ENDPOINTS,
  AskLubavButtonEndpoint,
} from '@backrs_ui/common-ui/src/models/nugget';
import {
  AskLubavButtonGroup,
  AskLubavButtonGroupProps,
  ButtonConfig,
  MappedButtonConfigs,
} from '@backrs_ui/common-ui/src/molecules/AskLubavButtons/AskLubavButtonGroup';
import { ApiKeyAction } from '@backrs_ui/common-ui/src/molecules/AskLubavButtons/AskLubavResponse';

import { Box } from '@mui/material';

import { CareerPathData } from './CareerPathData';

const CAREER_PATH_STRINGS = appStrings.modules.nugget.careerPath;

type NuggetCareerPathProps = Readonly<{
  careerPathInfo: CareerPath | null;
  isDialogOpen: boolean;
  tier: Tier | undefined;
  askLubavButtonGroupProps?: Omit<AskLubavButtonGroupProps, 'buttonConfigs'>;
  apiKeyOnClick: ApiKeyAction[];
  askLubavButtonResponse?: string;
}>;

export const NuggetCareerPath = ({
  careerPathInfo,
  askLubavButtonGroupProps,
  apiKeyOnClick,
  askLubavButtonResponse,
}: NuggetCareerPathProps): ReactElement => {
  const buttonConfigs: MappedButtonConfigs = new Map<AskLubavButtonEndpoint, ButtonConfig>([
    [
      ASK_LUBAV_BUTTON_ENDPOINTS.CAREER_TRACK_THREE_ROLES_FOR_ME,
      {
        buttonTitle: CAREER_PATH_STRINGS.threeRoles(careerPathInfo?.name || ''),
        disabledInProd: false,
        onClick: apiKeyOnClick.find(
          (x: ApiKeyAction): x is ApiKeyAction =>
            x?.key === ASK_LUBAV_BUTTON_ENDPOINTS.CAREER_TRACK_THREE_ROLES_FOR_ME,
        )?.onClick,
        actionButtonText: apiKeyOnClick.find(
          (x: ApiKeyAction): x is ApiKeyAction =>
            x?.key === ASK_LUBAV_BUTTON_ENDPOINTS.CAREER_TRACK_THREE_ROLES_FOR_ME,
        )?.actionButtonText,
      },
    ],
    [
      ASK_LUBAV_BUTTON_ENDPOINTS.CAREER_TRACK_ROLES_BY_TRACK,
      {
        buttonTitle: CAREER_PATH_STRINGS.rolesByTrack,
        disabledInProd: false,
        onClick: apiKeyOnClick.find(
          (x: ApiKeyAction): x is ApiKeyAction =>
            x?.key === ASK_LUBAV_BUTTON_ENDPOINTS.CAREER_TRACK_ROLES_BY_TRACK,
        )?.onClick,
        actionButtonText: apiKeyOnClick.find(
          (x: ApiKeyAction): x is ApiKeyAction =>
            x?.key === ASK_LUBAV_BUTTON_ENDPOINTS.CAREER_TRACK_ROLES_BY_TRACK,
        )?.actionButtonText,
      },
    ],
    [
      ASK_LUBAV_BUTTON_ENDPOINTS.CAREER_TRACK_COMPENSATION,
      {
        buttonTitle: CAREER_PATH_STRINGS.compensationPotential,
        disabledInProd: false,
        onClick: apiKeyOnClick.find(
          (x: ApiKeyAction): x is ApiKeyAction =>
            x?.key === ASK_LUBAV_BUTTON_ENDPOINTS.CAREER_TRACK_COMPENSATION,
        )?.onClick,
        actionButtonText: apiKeyOnClick.find(
          (x: ApiKeyAction): x is ApiKeyAction =>
            x?.key === ASK_LUBAV_BUTTON_ENDPOINTS.CAREER_TRACK_COMPENSATION,
        )?.actionButtonText,
      },
    ],
    [
      ASK_LUBAV_BUTTON_ENDPOINTS.CAREER_TRACK_REQUIREMENTS_TO_SUCCEED,
      {
        buttonTitle: CAREER_PATH_STRINGS.requirements,
        disabledInProd: false,
        onClick: apiKeyOnClick.find(
          (x: ApiKeyAction): x is ApiKeyAction =>
            x?.key === ASK_LUBAV_BUTTON_ENDPOINTS.CAREER_TRACK_REQUIREMENTS_TO_SUCCEED,
        )?.onClick,
        actionButtonText: apiKeyOnClick.find(
          (x: ApiKeyAction): x is ApiKeyAction =>
            x?.key === ASK_LUBAV_BUTTON_ENDPOINTS.CAREER_TRACK_REQUIREMENTS_TO_SUCCEED,
        )?.actionButtonText,
      },
    ],
    [
      ASK_LUBAV_BUTTON_ENDPOINTS.CAREER_TRACK_AM_I_ON_TRACK,
      {
        buttonTitle: CAREER_PATH_STRINGS.onTrack,
        disabledInProd: false,
        requirements: {
          requireUser: true,
          requiresResume: true,
        },
        onClick: apiKeyOnClick.find(
          (x: ApiKeyAction): x is ApiKeyAction =>
            x?.key === ASK_LUBAV_BUTTON_ENDPOINTS.CAREER_TRACK_AM_I_ON_TRACK,
        )?.onClick,
        actionButtonText: apiKeyOnClick.find(
          (x: ApiKeyAction): x is ApiKeyAction =>
            x?.key === ASK_LUBAV_BUTTON_ENDPOINTS.CAREER_TRACK_AM_I_ON_TRACK,
        )?.actionButtonText,
      },
    ],
    [
      ASK_LUBAV_BUTTON_ENDPOINTS.CAREER_TRACK_AI_DISRUPTION,
      {
        buttonTitle: CAREER_PATH_STRINGS.aiDisruption,
        disabledInProd: true,
        onClick: apiKeyOnClick.find(
          (x: ApiKeyAction): x is ApiKeyAction =>
            x?.key === ASK_LUBAV_BUTTON_ENDPOINTS.CAREER_TRACK_AI_DISRUPTION,
        )?.onClick,
        actionButtonText: apiKeyOnClick.find(
          (x: ApiKeyAction): x is ApiKeyAction =>
            x?.key === ASK_LUBAV_BUTTON_ENDPOINTS.CAREER_TRACK_AI_DISRUPTION,
        )?.actionButtonText,
      },
    ],
  ]);

  return (
    <Box>
      {askLubavButtonGroupProps && (
        <AskLubavButtonGroup
          {...askLubavButtonGroupProps}
          buttonConfigs={buttonConfigs}
          askLubavButtonResponse={askLubavButtonResponse}
        />
      )}
      <CareerPathData careerPathInfo={careerPathInfo} />
    </Box>
  );
};
